/* @flow */
/*
This code is derived from react-switch by Markus Englund.
https://github.com/markusenglund/react-switch

MIT License

Copyright (c) 2017 Markus Englund

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import * as React from "react";
import { mix } from "polished";
import { withColorsProvider, type ColorSet } from "../ColorsProvider";
import prefixStyles from "../utils/prefixStyles";
import Tappable from "../Tappable";
import { killScroll, reviveScroll } from "../ScrollableDiv";

const MIN_DRAG_THRESHOLD = 2;

type Props = {
  // False positive...
  /* eslint-disable react/no-unused-prop-types */
  checked: boolean,
  onChange: (newValue: boolean) => void,
  disabled: boolean,
  offColor?: ?string,
  onColor?: ?string,
  offHandleColor?: ?string,
  onHandleColor?: ?string,
  handleDiameter?: ?number,
  boxShadow?: ?string,
  activeBoxShadow?: ?string,
  height: number,
  width: number,
  id?: ?string,
  "aria-labelledby"?: ?string,
  "aria-label"?: ?string,
  changeOnTap: ?boolean,
  offBackgroundText: React.Node,
  onBackgroundText: React.Node,
  colors: ColorSet,
  /* eslint-enable react/no-unused-prop-types */
};

type State = {
  currentPosition: number,
  startX: ?number,
  isDragging: boolean,
  hasOutline: boolean,
};

class Switch extends React.Component<Props, State> {
  static defaultProps = {
    disabled: false,
    handleDiameter: null,
    boxShadow: null,
    activeBoxShadow: "0px 0px 2px 3px #33bbff",
    height: 28,
    width: 56,
    className: null,
    id: null,
    "aria-labelledby": null,
    "aria-label": null,
    changeOnTap: true,
    offBackgroundText: null,
    onBackgroundText: null,
  };

  _handleDiv: ?HTMLDivElement;
  _isTouchDevice: boolean = "ontouchstart" in window;

  _getHandleDiameter(props: Props): number {
    const { height, handleDiameter } = props;
    return handleDiameter || height - 2;
  }

  _getCheckedPos(props: Props): number {
    const handleDiameter = this._getHandleDiameter(props);
    const { width, height } = props;
    return Math.max(width - height, width - (height + handleDiameter) / 2);
  }

  _getUncheckedPos(props: Props): number {
    const handleDiameter = this._getHandleDiameter(props);
    const { height } = props;
    return Math.max(0, (height - handleDiameter) / 2);
  }

  state = {
    currentPosition: this.props.checked
      ? this._getCheckedPos(this.props)
      : this._getUncheckedPos(this.props),
    startX: null,
    isDragging: false,
    hasOutline: false,
  };

  _getAmountDraggedRatio(props: Props, state: State) {
    const uncheckedPos = this._getUncheckedPos(props);
    const checkedPos = this._getCheckedPos(props);
    const { currentPosition } = state;
    return (currentPosition - uncheckedPos) / (checkedPos - uncheckedPos);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const currentPosition = nextProps.checked
      ? this._getCheckedPos(nextProps)
      : this._getUncheckedPos(nextProps);
    this.setState({ currentPosition });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.isDragging === prevState.isDragging) {
      return;
    }

    if (this.state.isDragging) {
      killScroll();
    } else {
      reviveScroll();
    }
  }

  _handleDragStart = (clientX: number) => {
    this.setState({ startX: clientX, hasOutline: true });
  };

  _handleDrag = (clientX: number) => {
    const { startX } = this.state;
    if (startX == null) {
      return;
    }
    const { checked } = this.props;
    const checkedPos = this._getCheckedPos(this.props);
    const uncheckedPos = this._getUncheckedPos(this.props);
    const startPos = checked ? checkedPos : uncheckedPos;
    const newPos = startPos + clientX - startX;
    const currentPosition = Math.min(
      checkedPos,
      Math.max(uncheckedPos, newPos)
    );
    const isDragging = Math.abs(clientX - startX) > MIN_DRAG_THRESHOLD;
    this.setState({ currentPosition, isDragging });
  };

  _handleDragEnd = () => {
    const { currentPosition, isDragging } = this.state;
    const { checked, onChange, changeOnTap } = this.props;

    if (!isDragging && changeOnTap) {
      this.setState({ startX: null, hasOutline: false });
      onChange(!checked);
      return;
    }
    const checkedPos = this._getCheckedPos(this.props);
    const uncheckedPos = this._getUncheckedPos(this.props);
    if (checked) {
      if (currentPosition > (checkedPos + uncheckedPos) / 2) {
        // Dragged less than halfway; revert
        this.setState({
          currentPosition: checkedPos,
          startX: null,
          isDragging: false,
          hasOutline: false,
        });
        return;
      }
      this.setState({ startX: null, isDragging: false, hasOutline: false });
      onChange(false);
    } else {
      if (currentPosition <= (checkedPos + uncheckedPos) / 2) {
        // Dragged less than (or equal to) halfway; revert
        this.setState({
          currentPosition: uncheckedPos,
          startX: null,
          isDragging: false,
          hasOutline: false,
        });
        return;
      }
      this.setState({ startX: null, isDragging: false, hasOutline: false });
      onChange(true);
    }
  };

  _handleMouseDown = (event: SyntheticMouseEvent<HTMLDivElement>) => {
    // Ignore right click and scroll
    if (typeof event.button === "number" && event.button !== 0) {
      return;
    }

    this._handleDragStart(event.clientX);
    document.addEventListener("mousemove", this._handleMouseMove);
    document.addEventListener("mouseup", this._handleMouseUp);
  };

  _handleMouseMove = (event: MouseEvent) => {
    event.preventDefault();
    this._handleDrag(event.clientX);
  };

  _handleMouseUp = () => {
    this._handleDragEnd();
    document.removeEventListener("mousemove", this._handleMouseMove);
    document.removeEventListener("mouseup", this._handleMouseUp);
  };

  _handleTouchStart = (event: SyntheticTouchEvent<HTMLDivElement>) => {
    this._handleDragStart(event.touches[0].clientX);
  };

  _handleTouchMove = (event: SyntheticTouchEvent<HTMLDivElement>) => {
    this._handleDrag(event.touches[0].clientX);
  };

  _handleTouchEnd = (event: SyntheticTouchEvent<HTMLDivElement>) => {
    event.preventDefault();
    this._handleDragEnd();
  };

  _handleTouchCancel = () => {
    this.setState({ startX: null, hasOutline: false, isDragging: false });
  };

  _handleTap = () => {
    this._handleDragEnd();
  };

  _handleKeyDown = (event: SyntheticKeyboardEvent<HTMLDivElement>) => {
    const { checked, onChange } = this.props;
    const { isDragging } = this.state;
    const spaceKeyCode = 32;
    const enterKeyCode = 13;
    if (
      (event.keyCode === spaceKeyCode || event.keyCode === enterKeyCode) &&
      !isDragging
    ) {
      event.preventDefault();
      onChange(!checked);
    }
  };

  componentWillUnmount() {
    document.removeEventListener("mousemove", this._handleMouseMove);
    document.removeEventListener("mouseup", this._handleMouseUp);
  }

  render() {
    const {
      checked,
      disabled,
      boxShadow,
      activeBoxShadow,
      height,
      width,
      id,
      "aria-labelledby": ariaLabelledby,
      "aria-label": ariaLabel,
      changeOnTap,
      offBackgroundText,
      onBackgroundText,
      colors,
    } = this.props;
    const { currentPosition, isDragging, hasOutline } = this.state;
    const handleDiameter = this._getHandleDiameter(this.props);
    const amountDraggedRatio = this._getAmountDraggedRatio(
      this.props,
      this.state
    );

    const offColor = this.props.offColor || "#888";
    const onColor = this.props.onColor || colors.PRIMARY;
    const offHandleColor = this.props.offHandleColor || "#fff";
    const onHandleColor = this.props.onHandleColor || "#fff";

    const styles = prefixStyles({
      root: {
        position: "relative",
        display: "inline-block",
        opacity: disabled ? 0.5 : 1,
        borderRadius: height / 2,
        transition: "opacity 0.25s",
        WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
        userSelect: "none",
      },
      background: {
        height,
        width,
        margin: Math.max(0, (handleDiameter - height) / 2),
        position: "relative",
        backgroundColor: mix(amountDraggedRatio, onColor, offColor),
        borderRadius: height / 2,
        cursor: changeOnTap && !disabled ? "pointer" : "default",
        transition: isDragging ? null : "background 0.25s",
      },
      offBackgroundTextWrapper: {
        position: "absolute",
        width: "100%",
        opacity: 1 - amountDraggedRatio * 2,
        textAlign: "center",
        color: "white",
        fontSize: "0.8em",
        lineHeight: `${height}px`,
        pointerEvents: "none",
      },
      onBackgroundTextWrapper: {
        position: "absolute",
        width: "100%",
        opacity: amountDraggedRatio * 2 - 1,
        textAlign: "center",
        color: "white",
        fontSize: "0.8em",
        lineHeight: `${height}px`,
        pointerEvents: "none",
      },
      handle: {
        height: handleDiameter,
        width: handleDiameter,
        backgroundColor: mix(amountDraggedRatio, onHandleColor, offHandleColor),
        cursor: disabled ? "default" : "pointer",
        display: "inline-block",
        borderRadius: "50%",
        position: "absolute",
        transform: `translateX(${currentPosition}px)`,
        top: Math.max(0, (height - handleDiameter) / 2),
        left: 0, // TODO: This makes them look good in mobile, but we shouldn't need it?
        outline: 0,
        boxShadow: hasOutline ? activeBoxShadow : boxShadow,
        border: 0,
        transition: isDragging
          ? null
          : "background-color 0.25s, transform 0.25s, box-shadow 0.15s",
        touchAction: isDragging ? "none" : "",
      },
    });

    const backgroundContent = (refHandler) => (
      <div
        data-component-section="background"
        style={styles.background}
        ref={refHandler}
      >
        {offBackgroundText ? (
          <div
            data-component-section="off-background-text-wrapper"
            style={styles.offBackgroundTextWrapper}
          >
            {offBackgroundText}
          </div>
        ) : null}
        {onBackgroundText ? (
          <div
            data-component-section="on-background-text-wrapper"
            style={styles.onBackgroundTextWrapper}
          >
            {onBackgroundText}
          </div>
        ) : null}
      </div>
    );

    let dragEventHandlerProps;
    if (disabled) {
      dragEventHandlerProps = {};
    } else if (this._isTouchDevice) {
      dragEventHandlerProps = {
        onTouchStart: disabled ? null : this._handleTouchStart,
        onTouchMove: disabled ? null : this._handleTouchMove,
        onTouchEnd: disabled ? null : this._handleTouchEnd,
        onTouchCancel: disabled ? null : this._handleTouchCancel,
      };
    } else {
      dragEventHandlerProps = {
        onMouseDown: disabled ? null : this._handleMouseDown,
      };
    }

    return (
      <div data-component-name="Switch" style={styles.root}>
        {changeOnTap && !disabled ? (
          <Tappable onTap={this._handleTap}>
            {({ tapTargetRefHandler }) =>
              backgroundContent(tapTargetRefHandler)
            }
          </Tappable>
        ) : (
          backgroundContent(undefined)
        )}
        <div
          data-component-section="handle"
          ref={(el) => (this._handleDiv = el)}
          role="checkbox"
          style={styles.handle}
          {...dragEventHandlerProps}
          tabIndex={disabled ? null : 0}
          onKeyDown={disabled ? null : this._handleKeyDown}
          onFocus={() => this.setState({ hasOutline: true })}
          onBlur={() => this.setState({ hasOutline: false })}
          id={id}
          aria-checked={checked}
          aria-disabled={disabled}
          aria-labelledby={ariaLabelledby}
          aria-label={ariaLabel}
        />
      </div>
    );
  }
}

export default withColorsProvider(Switch);



// WEBPACK FOOTER //
// ../nx-components/src/internal/Switch.jsx